/**
 * Branch Hub — the single screen a customer lands on after picking a branch.
 * Shows one big "main button" per service segment the branch actually offers (Tables, Rooms,
 * Tournaments), resolved from the branch's feature entitlements. Tapping a segment opens its flow.
 * Route: /customer/organizers/:organizerId/branches/:branchId
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, BedDouble, Trophy, ChevronRight, MapPin, Phone, ShoppingBag, CircleDot, UtensilsCrossed, Bell, Receipt, MessageSquare, Sparkles, QrCode, X, Info, Image as ImageIcon, Wine, Gift, Navigation, Share2, Wallet, CalendarClock } from 'lucide-react';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import { usePlatformFlags } from '@/context/PlatformFlagsContext';
import MenuImagesModal from '@/components/MenuImages';
import CallStaff from '@/components/CallStaff/CallStaff';
import TableServiceOrderModal from '@/components/TableServiceOrder/TableServiceOrderModal';
import BillModal from '@/components/TableBill';
import Concierge from '@/components/Concierge/Concierge';
import SendDrinkModal from '@/components/DrinkGifts/SendDrinkModal';
import DrinkGiftListener from '@/components/DrinkGifts/DrinkGiftListener';
import BranchAbout from '@/components/BranchAbout';
import BranchGallery from '@/components/BranchGallery';
import VipReserve from '@/components/VipReserve';
import styles from './BranchHub.module.scss';

interface Segment {
  key: string;
  title: string;
  desc: string;
  icon: React.ReactNode;
  accent: string;
  path: string;
  show: boolean;
}

const btKey = (bt: { code?: string; label?: string } | null) => `${bt?.code || ''} ${bt?.label || ''}`.toLowerCase();
const btHas = (bt: { code?: string; label?: string } | null, ...ws: string[]) => { const k = btKey(bt); return ws.some((w) => k.includes(w)); };

/**
 * Domain-appropriate wording for the "seating/tables" segment. Hospitality types (bar, restaurant,
 * lounge, café, hotel) are matched FIRST so a bar-with-billiards reads as a place to sit & drink,
 * not "Pool Tables". A pure billiards/snooker hall (no dining keyword) is the only "Pool Tables" case.
 */
function tablesSegment(bt: { code?: string; label?: string } | null): { title: string; desc: string; icon: React.ReactNode } {
  const has = (...ws: string[]) => btHas(bt, ...ws);
  if (has('grocery', 'mart', 'supermarket', 'kirana'))
    return { title: 'Shopping', desc: 'Send a shopping request and chat with staff.', icon: <ShoppingBag size={26} /> };
  if (has('bar', 'pub', 'brew', 'tavern'))
    return { title: 'Grab a Table', desc: 'Take a seat, then chat & order drinks and food.', icon: <LayoutGrid size={26} /> };
  if (has('restaurant', 'dining', 'diner', 'eatery', 'food'))
    return { title: 'Dining Tables', desc: 'Reserve a table, then chat & order food.', icon: <LayoutGrid size={26} /> };
  if (has('lounge', 'club'))
    return { title: 'Lounge Seating', desc: 'Reserve a spot, then chat & order.', icon: <LayoutGrid size={26} /> };
  if (has('cafe', 'café', 'coffee', 'bistro'))
    return { title: 'Café Tables', desc: 'Grab a table, then chat & order.', icon: <LayoutGrid size={26} /> };
  if (has('hotel', 'resort', 'inn'))
    return { title: 'Dining Tables', desc: 'Reserve a dining table, then chat & order.', icon: <LayoutGrid size={26} /> };
  if (has('salon', 'saloon', 'spa', 'barber', 'beauty'))
    return { title: 'Book a Chair', desc: 'Reserve a chair and chat with the team.', icon: <LayoutGrid size={26} /> };
  // Game / sports venues (non-alcohol) — the "tables" segment reads as a GAME action, and the
  // order tagline stays snacks / tea / coffee (never drinks/alcohol). The single "Games / Sports"
  // type (code 'games') is generic; any legacy per-game types keep their specific wording below.
  if (has('games', 'sport'))
    return { title: 'Game Tables / Boards', desc: 'Book a table or board, then chat & order snacks, tea or coffee.', icon: <CircleDot size={26} /> };
  if (has('snooker'))
    return { title: 'Snooker Tables', desc: 'Book a table, then chat & order snacks and tea.', icon: <CircleDot size={26} /> };
  if (has('chess'))
    return { title: 'Chess Boards', desc: 'Book a board, then chat & order snacks, tea or coffee.', icon: <CircleDot size={26} /> };
  if (has('carrom'))
    return { title: 'Carrom Boards', desc: 'Book a board, then chat & order snacks and tea.', icon: <CircleDot size={26} /> };
  if (has('foosball', 'foos', 'table football'))
    return { title: 'Foosball Tables', desc: 'Book a table, then chat & order snacks and cold drinks.', icon: <CircleDot size={26} /> };
  if (has('dart'))
    return { title: 'Dartboards', desc: 'Book a board, then chat & order snacks and tea.', icon: <CircleDot size={26} /> };
  if (has('billiard', 'pool', 'cue'))
    return { title: 'Pool Tables', desc: 'Book a table, then chat & order snacks.', icon: <CircleDot size={26} /> };
  if (bt?.label) return { title: `${bt.label} Tables`, desc: 'Reserve a table, then chat & order.', icon: <LayoutGrid size={26} /> };
  return { title: 'Book a Table', desc: 'Reserve a table, then chat & order.', icon: <LayoutGrid size={26} /> };
}

/** A light visual identity per branch type — accent colour, emoji and a one-line tagline for the hub hero. */
function branchTheme(bt: { code?: string; label?: string } | null): { accent: string; emoji: string; tagline: string } {
  const has = (...ws: string[]) => btHas(bt, ...ws);
  if (has('grocery', 'mart', 'supermarket', 'kirana', 'shop', 'store')) return { accent: '#16a34a', emoji: '🛒', tagline: 'Shop and send your list — we’ll take care of it.' };
  if (has('bar', 'pub', 'brew', 'tavern')) return { accent: '#b45309', emoji: '🍺', tagline: 'Grab a table, order drinks & bites, and enjoy.' };
  if (has('restaurant', 'dining', 'diner', 'eatery', 'food')) return { accent: '#dc2626', emoji: '🍽️', tagline: 'Reserve a table and order from the menu.' };
  if (has('hotel', 'resort', 'inn')) return { accent: '#0891b2', emoji: '🏨', tagline: 'Book a room or a table — we’ll look after you.' };
  if (has('cafe', 'café', 'coffee', 'bistro')) return { accent: '#92400e', emoji: '☕', tagline: 'Cozy up — grab a table and order.' };
  if (has('lounge', 'club')) return { accent: '#7c3aed', emoji: '🛋️', tagline: 'Relax, reserve a spot and order.' };
  if (has('salon', 'saloon', 'spa', 'barber', 'beauty')) return { accent: '#db2777', emoji: '💇', tagline: 'Book your visit and chat with the team.' };
  // Game / sports venues (non-alcohol). Generic 'games' first, then any legacy per-game types.
  if (has('games', 'sport')) return { accent: '#15803d', emoji: '🎯', tagline: 'Book a table or board, play your game and order snacks & tea.' };
  if (has('snooker')) return { accent: '#166534', emoji: '🎱', tagline: 'Book a table, play a frame and order snacks & tea.' };
  if (has('chess')) return { accent: '#475569', emoji: '♟️', tagline: 'Book a board, play your game and order snacks, tea or coffee.' };
  if (has('carrom')) return { accent: '#d97706', emoji: '🔴', tagline: 'Book a board, play a game and order snacks & tea.' };
  if (has('foosball', 'foos', 'table football')) return { accent: '#2563eb', emoji: '⚽', tagline: 'Book a table, play a match and order snacks & cold drinks.' };
  if (has('dart')) return { accent: '#dc2626', emoji: '🎯', tagline: 'Book a board, throw a few and order snacks & tea.' };
  if (has('billiard', 'pool', 'cue')) return { accent: '#15803d', emoji: '🎱', tagline: 'Rack ’em up — book a table and order snacks.' };
  return { accent: '#2563eb', emoji: '🏢', tagline: 'Pick what you’d like to do below.' };
}

const BranchHub: React.FC = () => {
  const { organizerId, branchId } = useParams<{ organizerId: string; branchId: string }>();
  const navigate = useNavigate();
  const platformFlags = usePlatformFlags();

  const [branchName, setBranchName] = useState('');
  const [organizerName, setOrganizerName] = useState('');
  const [organizerLogo, setOrganizerLogo] = useState('');
  const [branchAddress, setBranchAddress] = useState('');
  const [branchPhone, setBranchPhone] = useState('');
  const [branchType, setBranchType] = useState<{ code?: string; label?: string } | null>(null);
  const [features, setFeatures] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);

  // Registered-customer service tools. The menu is always viewable; the live tools
  // (order / call staff / bill / concierge) only appear once the customer is actually
  // seated here — i.e. has a checked-in ('active') booking at THIS branch, whether they
  // got there by QR scan or by booking a table in the app. Same components the QR flow uses.
  const [seated, setSeated] = useState<{ bookingId: string; tableName: string } | null>(null);
  const [showMenu, setShowMenu] = useState(false);      // real inventory menu (view-only browse)
  const [showAbout, setShowAbout] = useState(false);    // About Us (description, address, contact, hours)
  const [showGallery, setShowGallery] = useState(false); // members-only branch gallery
  const [showVip, setShowVip] = useState(false);         // bottle service / VIP tables
  const [showImages, setShowImages] = useState(false);  // static picture/poster menu
  const [showOrder, setShowOrder] = useState(false);
  const [showCall, setShowCall] = useState(false);
  const [showBill, setShowBill] = useState(false);
  const [showStartVisit, setShowStartVisit] = useState(false); // gate for table-bound tools when not seated
  const [rewards, setRewards] = useState<any[]>([]); // referral reward vouchers redeemable at this branch
  const [ageGate, setAgeGate] = useState<{ minAge: number } | null>(null); // age-check gate, when required + unconfirmed
  const [loyalty, setLoyalty] = useState<{ stamps: number; goal: number; rewardLabel: string | null } | null>(null);
  const [giftsEnabled, setGiftsEnabled] = useState(false); // "send a drink to another table" toggle
  const [showSendDrink, setShowSendDrink] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (!branchId) return;
    let alive = true;
    apiService.getMyBookings().then((res) => {
      if (!alive) return;
      const list: any[] = (res?.data?.bookings as any[]) || [];
      const active = list.find((b) => b.branchId === branchId && b.status === 'active');
      if (active) {
        const num = active.table?.tableNumber;
        setSeated({ bookingId: active.id, tableName: num != null ? `Table ${num}` : 'your table' });
      } else {
        setSeated(null);
      }
    }).catch(() => { /* not signed in / no bookings — menu stays viewable, tools stay hidden */ });
    return () => { alive = false; };
  }, [branchId]);

  useEffect(() => {
    if (!branchId) return;
    let alive = true;
    setLoading(true);
    Promise.allSettled([
      apiService.getBranchDetails(branchId),
      apiService.getBranchFeatures(branchId),
    ]).then(([detailsRes, featRes]) => {
      if (!alive) return;
      if (detailsRes.status === 'fulfilled') {
        const data: any = detailsRes.value?.data ?? detailsRes.value ?? {};
        const d: any = data.branch ?? data; // getBranchDetails nests the branch under data.branch
        setBranchName(d.name || d.branchName || 'Branch');
        setOrganizerName(d.organizerName || '');
        setOrganizerLogo(d.organizerLogo || '');
        const parts = [d.address, d.city, d.state].filter(Boolean);
        setBranchAddress(parts.join(', '));
        setBranchPhone(d.phone || d.contact || d.contact_number || '');
        if (d.branchType) setBranchType({ code: d.branchType.code, label: d.branchType.label });
        // Age check: if the branch serves alcohol and requires it, gate on a one-time self-declaration.
        const servesAlcohol = d.servesAlcohol !== false;
        const ageOn = d.ageCheckEnabled === true && servesAlcohol;
        const minAge = Number(d.minAge) || 21;
        let confirmed = false;
        try { confirmed = localStorage.getItem(`agecheck-${branchId}`) === 'ok'; } catch { /* ignore */ }
        setAgeGate(ageOn && !confirmed ? { minAge } : null);
        setGiftsEnabled(d.giftsEnabled === true);
      }
      if (featRes.status === 'fulfilled' && Array.isArray(featRes.value?.features)) {
        setFeatures(featRes.value.features);
      } else {
        // Fail CLOSED: if features can't be resolved, default to just Tables and deliberately OMIT
        // 'room_service' AND 'tournaments' — showing a spurious "Rooms"/"Tournaments" button (e.g. at a
        // restaurant) is worse than hiding a real one. Those appear only when the branch actually
        // reports the entitlement.
        setFeatures(['table_service']);
      }
      setLoading(false);
    });
    return () => { alive = false; };
  }, [branchId]);

  const base = `/customer/organizers/${organizerId}/branches/${branchId}`;

  const theme = useMemo(() => branchTheme(branchType), [branchType]);

  const f = features || [];
  const hasTables = f.includes('table_service') || f.includes('billiards');
  const isAppointments = f.includes('appointments');

  // Appointment venues (salon/spa/barber/clinic) bill an APPOINTMENT, not a table — so the table/bar
  // live tools (menu ordering, VIP, call staff, bill, concierge) don't apply. Hide them and lead with
  // "Book an appointment" instead.
  const apptWording = btHas(branchType, 'spa') ? { title: 'Book a treatment', desc: 'Pick a treatment, therapist and time.' }
    : btHas(branchType, 'clinic', 'dental', 'medical') ? { title: 'Book an appointment', desc: 'Choose a service, specialist and time.' }
    : btHas(branchType, 'barber') ? { title: 'Book your cut', desc: 'Pick a service, barber and time.' }
    : { title: 'Book an appointment', desc: 'Pick a service, professional and time.' };

  const segments: Segment[] = useMemo(() => {
    const hasRooms = f.includes('room_service');
    const tSeg = tablesSegment(branchType);
    return [
      {
        key: 'appointments',
        title: apptWording.title,
        desc: apptWording.desc,
        icon: <CalendarClock size={26} />,
        accent: theme.accent,
        path: `${base}/appointments`,
        show: isAppointments,
      },
      {
        key: 'tables',
        title: tSeg.title,
        desc: tSeg.desc,
        icon: tSeg.icon,
        accent: theme.accent,
        path: `${base}/tables`,
        show: hasTables,
      },
      {
        key: 'rooms',
        title: 'Rooms',
        desc: 'Book a stay and use in-room services.',
        icon: <BedDouble size={26} />,
        accent: '#0891b2',
        path: `${base}/rooms`,
        show: hasRooms,
      },
      {
        key: 'tournaments',
        title: 'Tournaments',
        desc: 'View schedules, register and join matches.',
        icon: <Trophy size={26} />,
        accent: '#d97706',
        path: `${base}/tournaments`,
        // Global TOURNAMENTS module must be on (fail-closed) AND this branch must have the dedicated
        // `tournaments` entitlement — so a restaurant/café never shows it, but a bar/pub/games venue
        // does (independent of billiards). Backend gates /tournaments on the same `tournaments` feature.
        show: platformFlags.tournaments && f.includes('tournaments'),
      },
    ].filter((s) => s.show);
  }, [features, base, branchType, theme, platformFlags.tournaments]);

  // Registered customers: surface any unused reward voucher redeemable at THIS branch, to nudge
  // redemption at the moment of highest intent (they're here). Fails silently for QR guests.
  useEffect(() => {
    if (!branchId || !apiService.isAuthenticated()) { setRewards([]); return; }
    let alive = true;
    apiService.getReferralVouchers(branchId)
      .then((res: any) => { if (alive) setRewards(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => { if (alive) setRewards([]); });
    apiService.getLoyaltyCard(branchId)
      .then((res: any) => { if (alive) setLoyalty(res?.data || null); })
      .catch(() => { if (alive) setLoyalty(null); });
    return () => { alive = false; };
  }, [branchId]);

  // Share the venue via the OS share sheet (integration with every app the guest has), WhatsApp as
  // the fallback. Free word-of-mouth — a guest sends the venue straight to their friends.
  const shareVenue = async () => {
    const url = window.location.href;
    const title = organizerName || branchName || 'this venue';
    const text = `Check out ${title} on Chatup24`;
    if (typeof navigator !== 'undefined' && (navigator as any).share) {
      try { await (navigator as any).share({ title, text, url }); return; } catch { /* user cancelled */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, '_blank', 'noopener,noreferrer');
  };

  // Age-check gate: guest self-declares they're of legal age. Remembered per device; if they're
  // already seated we also record it on the booking (the staff ID check is the authoritative layer).
  const confirmAge = () => {
    try { localStorage.setItem(`agecheck-${branchId}`, 'ok'); } catch { /* ignore */ }
    if (seated?.bookingId) apiService.declareAge(seated.bookingId).catch(() => {});
    setAgeGate(null);
  };
  const declineAge = () => { navigate(`/customer/organizers/${organizerId}`); };

  return (
    <>
      <DashboardHeader />
      {ageGate && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 18, padding: 26, maxWidth: 380, width: '100%', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🔞</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>Are you {ageGate.minAge} or older?</h2>
            <p style={{ color: '#6b7280', fontSize: 14.5, margin: '0 0 18px', lineHeight: 1.5 }}>
              This venue serves alcohol. Please confirm you're of legal drinking age. Staff may check your ID.
            </p>
            <button onClick={confirmAge} style={{ width: '100%', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 12, padding: '13px 16px', fontWeight: 800, fontSize: 15, cursor: 'pointer', marginBottom: 10 }}>
              Yes, I'm {ageGate.minAge}+
            </button>
            <button onClick={declineAge} style={{ width: '100%', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 12, padding: '12px 16px', fontWeight: 700, cursor: 'pointer' }}>
              No / go back
            </button>
          </div>
        </div>
      )}
      <div className={styles.page}>
        <div className={styles.content}>
          <button className={styles.back} onClick={() => navigate(`/customer/organizers/${organizerId}`)}>
            <ArrowLeft size={16} /> Back to branches
          </button>

          <div className={styles.header} style={{ borderTop: `4px solid ${theme.accent}` }}>
            {organizerLogo
              ? <img src={organizerLogo} alt={organizerName || branchName} className={styles.orgLogo} />
              : <span className={styles.orgEmoji} style={{ background: `${theme.accent}1a`, color: theme.accent }}>{theme.emoji}</span>}
            <div className={styles.headerText}>
              <h1 className={styles.branchName}>{organizerName || branchName || 'Branch'}</h1>
              {branchType?.label && <span className={styles.typeChip} style={{ background: `${theme.accent}1a`, color: theme.accent }}>{theme.emoji} {branchType.label}</span>}
              <div className={styles.meta}>
                {(organizerName && branchName) && <span className={styles.metaItem}><MapPin size={14} /> {branchName}{branchAddress ? ` · ${branchAddress}` : ''}</span>}
                {(!organizerName && branchAddress) && <span className={styles.metaItem}><MapPin size={14} /> {branchAddress}</span>}
                {branchPhone && <a className={styles.metaItem} href={`tel:${branchPhone.replace(/[^\d+]/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}><Phone size={14} /> {branchPhone}</a>}
              </div>
            </div>
          </div>

          {rewards.length > 0 && (() => {
            const v = rewards[0];
            const daysList = rewards
              .map((r) => (r.expiresAt ? Math.ceil((new Date(r.expiresAt).getTime() - Date.now()) / 86400000) : null))
              .filter((d): d is number => d != null);
            const daysLeft = daysList.length ? Math.min(...daysList) : null;
            const label = v.rewardType === 'percent' ? `${Number(v.rewardValue)}% off` : `${Number(v.rewardValue)} off`;
            return (
              <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 12, padding: '12px 14px', margin: '0 0 12px', display: 'flex', gap: 10, alignItems: 'center' }}>
                <Gift size={22} color="#c2410c" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, color: '#9a3412' }}>You have a reward here — {label}{rewards.length > 1 ? ` (+${rewards.length - 1} more)` : ''}</div>
                  <div style={{ fontSize: 12.5, color: '#b45309' }}>
                    Show it at the table — it comes off your bill.{daysLeft != null && daysLeft <= 14 ? ` Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.` : ''}
                  </div>
                </div>
              </div>
            );
          })()}

          {loyalty && loyalty.goal > 0 && (() => {
            const stamps = Math.max(0, Math.min(loyalty.stamps, loyalty.goal));
            const left = Math.max(0, loyalty.goal - stamps);
            return (
              <div style={{ background: '#faf5ff', border: '1px solid #d8b4fe', borderRadius: 12, padding: '12px 14px', margin: '0 0 12px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontWeight: 800, color: '#6b21a8' }}>⭐ Loyalty card</span>
                  <span style={{ fontSize: 12.5, color: '#7e22ce' }}>
                    {left === 0 ? `${loyalty.rewardLabel || 'Reward'} unlocked!` : `${left} more visit${left === 1 ? '' : 's'} for ${loyalty.rewardLabel || 'your reward'}`}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {Array.from({ length: loyalty.goal }).map((_, i) => (
                    <span key={i} style={{
                      width: 18, height: 18, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, lineHeight: 1,
                      background: i < stamps ? '#a855f7' : 'transparent',
                      border: `1.5px solid ${i < stamps ? '#a855f7' : '#d8b4fe'}`,
                      color: '#fff',
                    }}>{i < stamps ? '★' : ''}</span>
                  ))}
                </div>
              </div>
            );
          })()}

          <p className={styles.tagline}>{theme.tagline}</p>

          {/* The full QR feature set is shown to registered customers. Menu and Chat with the venue work
              anytime. Ordering, calling staff, paying the bill and the AI concierge are for a guest AT a
              table (the server needs to know which table) — so when you're not seated yet, tapping one
              opens "start your visit" (book a table, or scan the table QR). Once seated they go live. */}
          {seated ? (
            <p className={styles.seatedNote}>
              ✅ You're checked in at <strong>{seated.tableName}</strong> — order, call staff, pay and chat with the concierge right here.
            </p>
          ) : isAppointments && !hasTables ? (
            <p className={styles.browseNote}>
              <strong>{apptWording.title}</strong> below — pick a service, a professional and a time. The team will confirm your visit.
            </p>
          ) : (
            <p className={styles.browseNote}>
              Browsing as a guest. <strong>Book a table</strong> (or scan the table QR when you arrive) to order, call staff, pay and use the AI concierge.
            </p>
          )}
          <div className={styles.servicesBar} style={{ ['--accent' as any]: theme.accent }}>
            {hasTables && (
              <button type="button" className={styles.svcBtn} onClick={() => setShowMenu(true)}>
                <UtensilsCrossed size={20} /><span>View Menu</span>
              </button>
            )}
            <button type="button" className={styles.svcBtn} onClick={() => navigate(`/customer/branches/${branchId}/chat`)}>
              <MessageSquare size={20} /><span>Chat</span>
            </button>
            <button type="button" className={styles.svcBtn} onClick={() => setShowGallery(true)}>
              <ImageIcon size={20} /><span>Gallery</span>
            </button>
            {hasTables && (
              <button type="button" className={styles.svcBtn} onClick={() => setShowVip(true)}>
                <Wine size={20} /><span>VIP</span>
              </button>
            )}
            <button type="button" className={styles.svcBtn} onClick={() => setShowAbout(true)}>
              <Info size={20} /><span>About</span>
            </button>
            <button type="button" className={styles.svcBtn} onClick={() => navigate(`/customer/invite/${branchId}`)}>
              <Gift size={20} /><span>Invite</span>
            </button>
            {(branchAddress || branchName) && (
              <button
                type="button"
                className={styles.svcBtn}
                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([branchName, branchAddress].filter(Boolean).join(' '))}`, '_blank', 'noopener,noreferrer')}
              >
                <Navigation size={20} /><span>Directions</span>
              </button>
            )}
            <button type="button" className={styles.svcBtn} onClick={shareVenue}>
              <Share2 size={20} /><span>Share</span>
            </button>
            <button type="button" className={styles.svcBtn} onClick={() => navigate(`/customer/wallet/${branchId}`)}>
              <Wallet size={20} /><span>Wallet</span>
            </button>
            {hasTables && (
              <button type="button" className={styles.svcBtn} onClick={() => seated ? setShowOrder(true) : setShowStartVisit(true)}>
                <ShoppingBag size={20} /><span>Order</span>
              </button>
            )}
            {hasTables && (
              <button type="button" className={styles.svcBtn} onClick={() => seated ? setShowCall(true) : setShowStartVisit(true)}>
                <Bell size={20} /><span>Call staff</span>
              </button>
            )}
            {hasTables && (
              <button type="button" className={styles.svcBtn} onClick={() => seated ? setShowBill(true) : setShowStartVisit(true)}>
                <Receipt size={20} /><span>Bill</span>
              </button>
            )}
            {seated && giftsEnabled && (
              <button type="button" className={styles.svcBtn} onClick={() => setShowSendDrink(true)}>
                <Gift size={20} /><span>Send a drink</span>
              </button>
            )}
            {hasTables && !seated && (
              <button type="button" className={styles.svcBtn} onClick={() => setShowStartVisit(true)}>
                <Sparkles size={20} /><span>AI Concierge</span>
              </button>
            )}
          </div>
          {seated && branchId && (
            <div className={styles.conciergeSlot}>
              <Concierge bookingId={seated.bookingId} branchId={branchId} />
            </div>
          )}

          <h2 className={styles.sectionTitle}>What would you like to do?</h2>

          {loading ? (
            <div className={styles.grid}>
              {[0, 1, 2].map((i) => <div key={i} className={styles.skeleton} />)}
            </div>
          ) : (
            <div className={styles.grid}>
              {segments.map((s) => (
                <button
                  key={s.key}
                  className={styles.segCard}
                  style={{ ['--accent' as any]: s.accent }}
                  onClick={() => navigate(s.path)}
                >
                  <span className={styles.segIcon}>{s.icon}</span>
                  <span className={styles.segText}>
                    <span className={styles.segTitle}>{s.title}</span>
                    <span className={styles.segDesc}>{s.desc}</span>
                  </span>
                  <ChevronRight className={styles.segChevron} size={20} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />

      {/* View Menu = the REAL inventory menu (items, prices, descriptions, photos) in browse mode.
          Same screen QR guests order from, minus add/send until you're seated. "Menu Images" inside
          it opens the static posters; the CTA sends the customer to book a table. */}
      {showMenu && branchId && (
        <TableServiceOrderModal
          branchId={branchId}
          viewOnly
          onClose={() => setShowMenu(false)}
          onViewMenu={() => setShowImages(true)}
          onBook={() => navigate(`${base}/tables`)}
        />
      )}
      {showImages && branchId && (
        <MenuImagesModal branchId={branchId} branchName={organizerName || branchName} onClose={() => setShowImages(false)} />
      )}
      {showAbout && branchId && (
        <BranchAbout branchId={branchId} branchName={organizerName || branchName} onClose={() => setShowAbout(false)} />
      )}
      {showGallery && branchId && (
        <BranchGallery branchId={branchId} branchName={organizerName || branchName} onClose={() => setShowGallery(false)} />
      )}
      {showVip && branchId && (
        <VipReserve branchId={branchId} branchName={organizerName || branchName} onClose={() => setShowVip(false)} />
      )}
      {showOrder && seated && branchId && (
        <TableServiceOrderModal
          branchId={branchId}
          bookingId={seated.bookingId}
          tableName={seated.tableName}
          onClose={() => setShowOrder(false)}
          onViewMenu={() => setShowImages(true)}
        />
      )}
      {showCall && seated && (
        <CallStaff bookingId={seated.bookingId} branchId={branchId} tableName={seated.tableName} branchType={branchType} onClose={() => setShowCall(false)} />
      )}
      {showBill && seated && (
        <BillModal bookingId={seated.bookingId} tableName={seated.tableName} onClose={() => setShowBill(false)} />
      )}

      {showSendDrink && seated && branchId && (
        <SendDrinkModal branchId={branchId} bookingId={seated.bookingId} onClose={() => setShowSendDrink(false)} />
      )}
      {/* While seated at a gift-enabled venue, listen for drinks/toasts sent to this table. */}
      {seated && branchId && giftsEnabled && (
        <DrinkGiftListener branchId={branchId} bookingId={seated.bookingId} />
      )}

      {showStartVisit && (
        <div className={styles.sheetOverlay} onClick={() => setShowStartVisit(false)}>
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <button type="button" className={styles.sheetClose} onClick={() => setShowStartVisit(false)} aria-label="Close"><X size={20} /></button>
            <h3 className={styles.sheetTitle}>Start your visit</h3>
            <p className={styles.sheetText}>
              Ordering, calling staff, your bill and the AI concierge are for a guest <strong>at a table</strong> —
              so the venue knows where you are. Book a table now, or scan the table's QR when you arrive.
            </p>
            <button type="button" className={styles.sheetPrimary} style={{ ['--accent' as any]: theme.accent }} onClick={() => { setShowStartVisit(false); navigate(`${base}/tables`); }}>
              <LayoutGrid size={18} /> Book a table
            </button>
            <div className={styles.sheetHint}>
              <QrCode size={16} /> Already here? Scan the QR code on your table to start instantly.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BranchHub;
